import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Sequence, SequenceStep } from '../types/campaign';
import { StepEditor } from './Sequences/StepEditor';
import { SequenceStepCard } from './Sequences/SequenceStepCard';
import { SequenceTimeline } from './Sequences/SequenceTimeline';
import { HelpIcon } from './HelpIcon';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';

export const SequenceBuilder: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: sequenceIdParam } = useParams<{ id: string }>();
  
  const sequenceId = sequenceIdParam;
  const isEditMode = !!sequenceId;

  const [sequence, setSequence] = useState<Partial<Sequence>>({
    name: '',
    description: '',
    status: 'draft',
    max_duration_days: undefined,
    allow_opt_out: 'any_step',
  });

  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [editingStep, setEditingStep] = useState<SequenceStep | null>(null);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationResult, setValidationResult] = useState<any>(null);

  // Fetch existing sequence if editing
  const { data: existingSequence, isLoading: isLoadingSequence } = useQuery<Sequence>({
    queryKey: ['sequence', sequenceId],
    queryFn: async () => {
      if (!sequenceId) throw new Error('Sequence ID is required');
      const response = await api.sequences.get(sequenceId);
      return response.data;
    },
    enabled: isEditMode && !!sequenceId,
  });

  // Fetch steps if editing
  const { data: existingSteps = [], isLoading: isLoadingSteps } = useQuery<SequenceStep[]>({
    queryKey: ['sequence-steps', sequenceId],
    queryFn: async () => {
      if (!sequenceId) throw new Error('Sequence ID is required');
      const response = await api.sequences.getSteps(sequenceId);
      return response.data || [];
    },
    enabled: isEditMode && !!sequenceId,
  });

  // Update sequence and steps when existing data loads
  useEffect(() => {
    if (existingSequence) {
      setSequence(existingSequence);
    }
  }, [existingSequence]);

  useEffect(() => {
    if (existingSteps.length > 0) {
      setSteps(existingSteps);
    }
  }, [existingSteps]);

  // Save sequence mutation
  const saveSequenceMutation = useMutation({
    mutationFn: async (data: Partial<Sequence>) => {
      if (isEditMode) {
        return await api.sequences.update(sequenceId!, data);
      } else {
        return await api.sequences.create(data);
      }
    },
    onSuccess: async (response) => {
      const savedSequence = response.data;
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      toast.success(`Sequence ${isEditMode ? 'updated' : 'created'} successfully`);
      
      // If we have steps to save, save them now
      if (steps.length > 0) {
        try {
          // Save steps one by one
          for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            if (step.id) {
              // Update existing step - only send fields that SequenceStepUpdate expects
              const updateData: any = {
                name: step.name,
                step_type: step.step_type,
                order: i,
              };
            // Only include template_id if it's set and not empty (not an empty string)
            if (step.template_id && typeof step.template_id === 'string' && step.template_id.trim() !== '') {
              updateData.template_id = step.template_id;
            } else if (step.step_type === 'email' || step.step_type === 'sms') {
              // For email/sms steps, if template_id is empty, don't include it (let validation handle it)
              // Don't set to null as that might cause validation issues
            }
              // Only include delay fields if they have valid values
              if (step.delay_hours !== undefined && step.delay_hours !== null) {
                updateData.delay_hours = step.delay_hours;
              }
              if (step.delay_days !== undefined && step.delay_days !== null) {
                updateData.delay_days = step.delay_days;
              }
              // Only include conditions if it's a valid object
              if (step.conditions && Object.keys(step.conditions).length > 0) {
                updateData.conditions = step.conditions;
              }
              
              await api.sequences.updateStep(step.id, updateData);
            } else {
              // Create new step - only send fields that SequenceStepCreate expects
              const stepData: any = {
                name: step.name,
                step_type: step.step_type,
                order: i,
                sequence_id: savedSequence.id,
              };
              // Only include template_id if it's set and not empty (not an empty string)
              if (step.template_id && typeof step.template_id === 'string' && step.template_id.trim() !== '') {
                stepData.template_id = step.template_id;
              }
              // Don't include template_id at all if it's empty (let backend handle optional field)
              // Only include delay fields if they have valid values
              if (step.delay_hours !== undefined && step.delay_hours !== null) {
                stepData.delay_hours = step.delay_hours;
              }
              if (step.delay_days !== undefined && step.delay_days !== null) {
                stepData.delay_days = step.delay_days;
              }
              // Only include conditions if it's a valid object
              if (step.conditions && Object.keys(step.conditions).length > 0) {
                stepData.conditions = step.conditions;
              }
              
              await api.sequences.addStep(savedSequence.id, stepData);
            }
          }
          queryClient.invalidateQueries({ queryKey: ['sequence-steps', savedSequence.id] });
        } catch (stepError: any) {
          // Log step saving error but don't fail the whole operation
          console.error('Error saving steps:', stepError);
          toast.error(`Sequence created but some steps failed to save: ${stepError.response?.data?.detail || stepError.message}`);
        }
      }
      
      navigate(`/sequences/${savedSequence.id}`);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save sequence';
      toast.error(errorMessage);
      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
      }
    },
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      if (!sequenceId) {
        throw new Error('Sequence must be saved before validation');
      }
      const response = await api.sequences.validate(sequenceId);
      return response.data;
    },
    onSuccess: (data) => {
      setValidationResult(data);
      if (data.valid) {
        toast.success('Sequence is valid');
      } else {
        toast.error('Sequence validation failed');
      }
    },
    onError: (error: any) => {
      toast.error(`Failed to validate sequence: ${error.response?.data?.detail || error.message}`);
    },
  });

  const validateSequence = (): boolean => {
    const errors: string[] = [];

    if (!sequence.name || sequence.name.trim() === '') {
      errors.push('Sequence name is required');
    }

    if (steps.length === 0) {
      errors.push('At least one step is required');
    }

    // Validate each step
    steps.forEach((step, index) => {
      if (!step.name || step.name.trim() === '') {
        errors.push(`Step ${index + 1}: Name is required`);
      }
      if ((step.step_type === 'email' || step.step_type === 'sms') && !step.template_id) {
        errors.push(`Step ${index + 1}: Template is required for ${step.step_type} steps`);
      }
      if (step.step_type === 'delay' && !step.delay_hours && !step.delay_days) {
        errors.push(`Step ${index + 1}: At least one delay value is required for delay steps`);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = () => {
    setValidationErrors([]);
    if (!validateSequence()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    // Prepare sequence data - only include fields that SequenceBase expects
    // Remove undefined values and ensure status is a valid string
    const sequenceData: any = {
      name: sequence.name || '',
      description: sequence.description || undefined,
      status: sequence.status || 'draft',
      allow_opt_out: sequence.allow_opt_out || 'any_step',
    };
    
    // Only include max_duration_days if it's set
    if (sequence.max_duration_days !== undefined && sequence.max_duration_days !== null) {
      sequenceData.max_duration_days = sequence.max_duration_days;
    }
    
    // Remove undefined values
    Object.keys(sequenceData).forEach(key => {
      if (sequenceData[key] === undefined) {
        delete sequenceData[key];
      }
    });
    
    saveSequenceMutation.mutate(sequenceData);
  };

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepEditor(true);
  };

  const handleEditStep = (step: SequenceStep) => {
    setEditingStep(step);
    setShowStepEditor(true);
  };

  const handleDeleteStep = (step: SequenceStep) => {
    if (window.confirm(`Are you sure you want to delete step "${step.name}"?`)) {
      if (step.id) {
        // Delete from backend
        api.sequences.deleteStep(step.id)
          .then(() => {
            setSteps(steps.filter((s) => s.id !== step.id));
            queryClient.invalidateQueries({ queryKey: ['sequence-steps', sequenceId] });
            toast.success('Step deleted successfully');
          })
          .catch((error: any) => {
            toast.error(`Failed to delete step: ${error.response?.data?.detail || error.message}`);
          });
      } else {
        // Just remove from local state
        setSteps(steps.filter((s) => s !== step));
      }
    }
  };

  const handleSaveStep = (stepData: Partial<SequenceStep>) => {
    if (editingStep) {
      // Update existing step
      const updatedSteps = steps.map((s) =>
        s === editingStep ? { ...s, ...stepData } : s
      );
      setSteps(updatedSteps);
      
      // If step has an ID, update in backend
      if (editingStep.id && sequenceId) {
        // Only send fields that SequenceStepUpdate expects
        const updateData: any = {};
        if (stepData.name !== undefined) updateData.name = stepData.name;
        if (stepData.step_type !== undefined) updateData.step_type = stepData.step_type;
        if (stepData.order !== undefined) updateData.order = stepData.order;
        // Only include template_id if it's set and not empty (not an empty string)
        if (stepData.template_id !== undefined) {
          if (stepData.template_id && typeof stepData.template_id === 'string' && stepData.template_id.trim() !== '') {
            updateData.template_id = stepData.template_id;
          }
          // Don't include template_id at all if it's empty (let backend handle optional field)
        }
        if (stepData.delay_hours !== undefined && stepData.delay_hours !== null) {
          updateData.delay_hours = stepData.delay_hours;
        }
        if (stepData.delay_days !== undefined && stepData.delay_days !== null) {
          updateData.delay_days = stepData.delay_days;
        }
        if (stepData.conditions !== undefined) {
          if (stepData.conditions && typeof stepData.conditions === 'object' && Object.keys(stepData.conditions).length > 0) {
            updateData.conditions = stepData.conditions;
          } else {
            updateData.conditions = {};
          }
        }
        
        api.sequences.updateStep(editingStep.id, updateData)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['sequence-steps', sequenceId] });
            toast.success('Step updated successfully');
          })
          .catch((error: any) => {
            toast.error(`Failed to update step: ${error.response?.data?.detail || error.message}`);
          });
      }
    } else {
      // Add new step
      const newStep: SequenceStep = {
        id: '', // Will be set when saved
        sequence_id: sequenceId || '',
        name: stepData.name || '',
        step_type: stepData.step_type || 'email',
        order: steps.length,
        template_id: stepData.template_id,
        delay_hours: stepData.delay_hours,
        delay_days: stepData.delay_days,
        conditions: stepData.conditions,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setSteps([...steps, newStep]);
    }
    setShowStepEditor(false);
    setEditingStep(null);
  };

  const handleReorderStep = (stepIndex: number, direction: 'up' | 'down') => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newSteps.length) {
      return;
    }

    // Swap steps
    [newSteps[stepIndex], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[stepIndex]];
    
    // Update orders
    newSteps.forEach((step, index) => {
      step.order = index;
    });

    setSteps(newSteps);

    // If sequence is saved, update backend
    if (sequenceId) {
      const stepOrders: Record<string, number> = {};
      newSteps.forEach((step, index) => {
        if (step.id) {
          stepOrders[step.id] = index;
        }
      });
      api.sequences.reorderSteps(sequenceId, stepOrders)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['sequence-steps', sequenceId] });
        })
        .catch((error: any) => {
          toast.error(`Failed to reorder steps: ${error.response?.data?.detail || error.message}`);
        });
    }
  };

  const handleValidate = () => {
    if (!sequenceId) {
      toast.error('Sequence must be saved before validation');
      return;
    }
    validateMutation.mutate();
  };

  // Only show loading state if we're actually editing and the queries are enabled
  if (isEditMode && (isLoadingSequence || isLoadingSteps)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/sequences')}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Sequences
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex items-center gap-2 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Sequence' : 'Create Sequence'}
            </h1>
            <TooltipProvider>
              {(() => {
                const overviewHelp = getHelpContent('sequenceOverview');
                return overviewHelp ? (
                  <HelpIcon
                    tooltip="Learn about sequences"
                    modalTitle={overviewHelp.title}
                    modalContent={overviewHelp.content}
                    size="md"
                  />
                ) : null;
              })()}
            </TooltipProvider>
          </div>

          {/* Info Banner for New Sequences */}
          {!isEditMode && (
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800 mb-1">
                    Getting Started with Sequences
                  </h3>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>
                      <strong>Important:</strong> Templates must be set to <strong>"active"</strong> status (not "draft") to appear in the step editor.
                    </p>
                    <p>
                      To create a sequence: Add steps (emails, SMS, or delays), configure timing, and set the sequence status to "active" when ready to use in campaigns.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <h3 className="text-sm font-medium text-red-800 mb-2">Validation Errors:</h3>
              <ul className="list-disc list-inside text-sm text-red-700">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Validation Results */}
          {validationResult && (
            <div className={`mb-6 p-4 border-l-4 rounded ${
              validationResult.valid ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
              <h3 className={`text-sm font-medium mb-2 ${
                validationResult.valid ? 'text-green-800' : 'text-red-800'
              }`}>
                {validationResult.valid ? 'Sequence is valid' : 'Validation failed'}
              </h3>
              {validationResult.errors && validationResult.errors.length > 0 && (
                <ul className="list-disc list-inside text-sm text-red-700">
                  {validationResult.errors.map((error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Sequence Form */}
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={sequence.name}
                onChange={(e) => setSequence({ ...sequence, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., Welcome Sequence"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={sequence.description || ''}
                onChange={(e) => setSequence({ ...sequence, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Describe this sequence..."
              />
            </div>

            {/* Status and Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <TooltipProvider>
                    {(() => {
                      const configHelp = getHelpContent('sequenceConfiguration');
                      return configHelp ? (
                        <HelpIcon
                          tooltip="Learn about sequence configuration"
                          modalTitle={configHelp.title}
                          modalContent={configHelp.content}
                          size="sm"
                          variant="subtle"
                        />
                      ) : null;
                    })()}
                  </TooltipProvider>
                </div>
                <select
                  value={sequence.status}
                  onChange={(e) => setSequence({ ...sequence, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Max Duration (days)
                  </label>
                  <TooltipProvider>
                    {(() => {
                      const configHelp = getHelpContent('sequenceConfiguration');
                      return configHelp ? (
                        <HelpIcon
                          tooltip="Learn about max duration"
                          modalTitle={configHelp.title}
                          modalContent={configHelp.content}
                          size="sm"
                          variant="subtle"
                        />
                      ) : null;
                    })()}
                  </TooltipProvider>
                </div>
                <input
                  type="number"
                  min="0"
                  value={sequence.max_duration_days || ''}
                  onChange={(e) =>
                    setSequence({
                      ...sequence,
                      max_duration_days: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="No limit"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Allow Opt-Out
                  </label>
                  <TooltipProvider>
                    {(() => {
                      const configHelp = getHelpContent('sequenceConfiguration');
                      return configHelp ? (
                        <HelpIcon
                          tooltip="Learn about opt-out settings"
                          modalTitle={configHelp.title}
                          modalContent={configHelp.content}
                          size="sm"
                          variant="subtle"
                        />
                      ) : null;
                    })()}
                  </TooltipProvider>
                </div>
                <select
                  value={sequence.allow_opt_out}
                  onChange={(e) => setSequence({ ...sequence, allow_opt_out: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="any_step">Any step</option>
                  <option value="first_step">First step only</option>
                  <option value="never">Never</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Steps Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-medium text-gray-900">Steps</h2>
              <TooltipProvider>
                {(() => {
                  const stepsHelp = getHelpContent('sequenceSteps');
                  return stepsHelp ? (
                    <HelpIcon
                      tooltip="Learn about sequence steps"
                      modalTitle={stepsHelp.title}
                      modalContent={stepsHelp.content}
                      size="md"
                      variant="subtle"
                    />
                  ) : null;
                })()}
              </TooltipProvider>
            </div>
            <button
              onClick={handleAddStep}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Step
            </button>
          </div>

          {steps.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No steps yet. Add your first step to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={step.id || index} className="flex items-start space-x-4">
                  <div className="flex-1">
                    <SequenceStepCard
                      step={step}
                      stepNumber={index + 1}
                      onEdit={handleEditStep}
                      onDelete={handleDeleteStep}
                      canEdit={true}
                    />
                  </div>
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleReorderStep(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleReorderStep(index, 'down')}
                      disabled={index === steps.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Timeline Preview */}
          {steps.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-medium text-gray-900">Timeline Preview</h3>
                <TooltipProvider>
                  {(() => {
                    const timelineHelp = getHelpContent('sequenceTimeline');
                    return timelineHelp ? (
                      <HelpIcon
                        tooltip="Learn about sequence timeline"
                        modalTitle={timelineHelp.title}
                        modalContent={timelineHelp.content}
                        size="sm"
                        variant="subtle"
                      />
                    ) : null;
                  })()}
                </TooltipProvider>
              </div>
              <SequenceTimeline steps={steps} />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleValidate}
              disabled={!sequenceId || validateMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              {validateMutation.isPending ? 'Validating...' : 'Validate'}
            </button>
            <TooltipProvider>
              {(() => {
                const validationHelp = getHelpContent('sequenceValidation');
                return validationHelp ? (
                  <HelpIcon
                    tooltip="Learn about sequence validation"
                    modalTitle={validationHelp.title}
                    modalContent={validationHelp.content}
                    size="sm"
                    variant="subtle"
                  />
                ) : null;
              })()}
            </TooltipProvider>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/sequences')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveSequenceMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveSequenceMutation.isPending ? 'Saving...' : isEditMode ? 'Update Sequence' : 'Create Sequence'}
            </button>
          </div>
        </div>
      </div>

      {/* Step Editor Modal */}
      {showStepEditor && (
        <StepEditor
          step={editingStep || undefined}
          sequenceId={sequenceId || ''}
          order={editingStep ? editingStep.order : steps.length}
          onSave={handleSaveStep}
          onCancel={() => {
            setShowStepEditor(false);
            setEditingStep(null);
          }}
          isOpen={showStepEditor}
        />
      )}
    </div>
  );
};

